import { useState } from 'react';
import Logo from '../components/Logo.jsx';
import { supabase } from '../lib/supabase.js';
import './SignIn.css';

/**
 * SignIn — two-step OTP entry point.
 *
 * Step 1: User enters name + email. We call signInWithOtp, which
 * emails them a 6-digit code (configured in Supabase's email template).
 * Their typed name is stored in Supabase user_metadata so we can greet
 * them by name in Home.
 *
 * Step 2: User enters the 6-digit code. We call verifyOtp to complete
 * sign-in. On success, AuthContext picks up the new session via its
 * onAuthStateChange listener and the app re-renders into the signed-in
 * state — no redirect needed.
 */
export default function SignIn() {
  const [step, setStep] = useState('request'); // 'request' | 'verify'
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function handleRequestCode(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const { error: signInError } = await supabase.auth.signInWithOtp({
      email,
      options: {
        // Stash the name on the auth user so we can show it in Home,
        // You, and the avatar without a separate profiles table.
        data: { name },
      },
    });

    setSubmitting(false);

    if (signInError) {
      setError(signInError.message);
      return;
    }

    setStep('verify');
  }

  async function handleVerifyCode(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const { error: verifyError } = await supabase.auth.verifyOtp({
      email,
      token: code.trim(),
      type: 'email',
    });

    setSubmitting(false);

    if (verifyError) {
      setError(verifyError.message);
      return;
    }

    // No navigate() needed — AuthContext's onAuthStateChange listener
    // will detect the new session and the app will re-render into
    // the signed-in state automatically.
  }

  async function handleResendCode() {
    setError(null);
    setSubmitting(true);

    const { error: resendError } = await supabase.auth.signInWithOtp({
      email,
      options: { data: { name } },
    });

    setSubmitting(false);

    if (resendError) {
      setError(resendError.message);
      return;
    }
  }

  // ───────────────────────── Step 2: enter code ─────────────────────────
  if (step === 'verify') {
    return (
      <div className="signin">
        <div className="signin-card">
          <Logo size={56} />
          <h1>Check your inbox</h1>
          <p>
            We sent a 6-digit code to <strong>{email}</strong>.<br />
            Enter it below to sign in.
          </p>

          <form
            className="signin-form signin-form--verify"
            onSubmit={handleVerifyCode}
          >
            <label className="signin-field">
              <span className="eyebrow">Sign-in code</span>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={code}
                onChange={(e) =>
                  setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
                }
                placeholder="123456"
                maxLength={6}
                autoComplete="one-time-code"
                autoFocus
                required
                disabled={submitting}
                className="signin-code-input"
              />
            </label>

            {error && (
              <p
                style={{
                  color: 'var(--terra-stripe)',
                  fontSize: '0.85rem',
                  textAlign: 'center',
                  margin: 0,
                }}
              >
                {error}
              </p>
            )}

            <button
              className="btn btn-primary signin-submit"
              type="submit"
              disabled={submitting || code.length !== 6}
            >
              {submitting ? 'Verifying…' : 'Sign in'}
            </button>
          </form>

          <button
            className="btn btn-ghost"
            onClick={handleResendCode}
            disabled={submitting}
            style={{ marginTop: 'var(--space-3)' }}
          >
            Resend code
          </button>

          <button
            className="btn btn-ghost"
            onClick={() => {
              setStep('request');
              setCode('');
              setError(null);
            }}
            disabled={submitting}
          >
            Use a different email
          </button>
        </div>
      </div>
    );
  }

  // ───────────────────────── Step 1: enter email ────────────────────────
  return (
    <div className="signin">
      <div className="signin-hero">
        <Logo size={68} />
        <span
          className="eyebrow"
          style={{ color: 'rgba(255,255,255,0.78)', marginTop: 'var(--space-3)' }}
        >
          McEachern &amp; Associates
        </span>
        <h1 className="signin-title">
          Plan your<br />
          <em>presentation</em>,<br />end to end.
        </h1>
        <p>Six tools, one workbook. Auto-saves as you go.</p>
      </div>

      <form className="signin-form" onSubmit={handleRequestCode}>
        <label className="signin-field">
          <span className="eyebrow">Your name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your Name"
            required
            autoComplete="name"
            disabled={submitting}
          />
        </label>
        <label className="signin-field">
          <span className="eyebrow">Email address</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.ca"
            required
            autoComplete="email"
            disabled={submitting}
          />
        </label>

        {error && (
          <p
            style={{
              color: 'var(--terra-stripe)',
              fontSize: '0.85rem',
              textAlign: 'center',
              margin: 0,
            }}
          >
            {error}
          </p>
        )}

        <button
          className="btn btn-primary signin-submit"
          type="submit"
          disabled={submitting}
        >
          {submitting ? 'Sending…' : 'Send me a sign-in code'}
        </button>
        <p className="signin-fineprint">
          We'll email a 6-digit code. No password to remember.
        </p>
      </form>
    </div>
  );
}
